import { EAssetSource, EAssetType, getRefId, type FindexInvestment, type FullInvestment } from 'findef'
import type { UseAssetFormLayout, UseAssetFormState } from '../types'
import HeaderSm from 'components/headers/sm'
import { match } from '@/utils/etc'
import Separator from 'components/separator'
import OwnershipTree from '@/modules/asset/components/ownership-tree'
import { useResponsive } from '~/composables/useResponsive'

const responsive = useResponsive();

export const assetInformationHeader: UseAssetFormLayout = {
  component: () => {
    return (props) => {
      const state = props.state.value

      const header = {
        ...(state.asset.type === EAssetType.ALTERNATIVE
          ? {
              title: 'Alternative Asset Information',
              subtitle: 'General alternative information'
            }
          : state.asset.type === EAssetType.REAL_ESTATE
            ? {
                title: 'Real Estate Information',
                subtitle: 'General real estate information'
              }
            : state.asset.type === EAssetType.COMMODITY
              ? {
                  title: 'Commodity Information',
                  subtitle: 'Search and select the ticker for the commodity you want to add'
                }
              : state.asset.type === EAssetType.CRYPTO
                ? {
                    title: 'Crypto Information',
                    subtitle: 'Search and select the ticker for the crypto you want to add'
                  }
                : state.asset.type === EAssetType.CASH
                  ? {
                    title: 'Cash Information',
                    subtitle: 'Add the value of this cash asset/stash/depo'
                  }
                  : !state.asset.listed
                    ? {
                        title: 'Unlisted Equity Information',
                        subtitle: 'General company information'
                      }
                    : {
                        title: 'Listed Equity Information',
                        subtitle: 'General company information'
                      })
      }

      return <HeaderSm title={header.title} subtitle={header.subtitle} />
    }
  }
}

export const investmentHeader: UseAssetFormLayout = {
  component: () => {
    return () => {
      const header = {
        title: 'Total Investment',
        subtitle: responsive.isSmall.value
          ? 'Details about your initial investment'
          : 'Add an aggregated investment that represents all asset events over time for a simple overview.'
      }
      return <HeaderSm title={header.title} subtitle={header.subtitle} />
    }
  }
}

export const returnOnInvestmentHeader: UseAssetFormLayout = {
  component: () => {
    return () => {
      const header = {
        title: 'Return on Investment',
        subtitle: 'Add the current value of your investment so we can calculate your ROI'
      }
      return <HeaderSm title={header.title} subtitle={header.subtitle} />
    }
  }
}

export const assetNameLayout = (
  state: UseAssetFormState
): UseAssetFormLayout => ({
  render:
    (state.asset.source === EAssetSource.IR || state.providerImport || state.asset.provider) ? 'asset.name' : state.asset.type === EAssetType.CRYPTO
      ? 'asset.cryptoQuote'
      : state.asset.type === EAssetType.EQUITY
        ? 'asset.companyProfile'
        : state.asset.type === EAssetType.COMMODITY
          ? 'asset.commodityQuote'
          : 'asset.name'
})

export const assetIdentifier = (
  state: UseAssetFormState
): UseAssetFormLayout => ({
  key: `identifier-${state.asset.type}`,
  memo: (state) => [state.asset.type, state.asset.listed],
  render: match(
    [state.asset.type, state.asset.source],
    [
      [[state.asset.type, EAssetSource.IR], 'asset.organizationNumber'],
      [[EAssetType.CRYPTO, state.asset.source], 'symbol'],
      [[EAssetType.COMMODITY, state.asset.source], 'symbol']
    ],
    'asset.organizationNumber'
  )
})

export const separator: UseAssetFormLayout = {
  component: () => () => <Separator size="s" variant="gray" />,
  key: 'separator'
}

export const ownershipStructure: UseAssetFormLayout = {
  key: 'ownershipStructure',
  needs: ['_id', 'parentId'],
  component: () => {
    return (props) => {
      const state = props.state.value
      const invId = getRefId(state._id)
      const parentId = getRefId(state.parentId)
      const imgId = state.image ? getRefId(state.image) : 'x'
      //const owned = !!props.state.ownedByCompany;
      const name =
        state.asset.name ||
        state.asset.cryptoQuote?.name ||
        state.asset.commodityQuote?.name ||
        state.asset.companyProfile?.companyName ||
        '?'
      const mock: (FindexInvestment | FullInvestment) = {
        _id: 'mock',
        invested: { value: 0, type: 'SEK' },
        returnValue: { value: 0, type: 'SEK' },
        quantity: 1,
        ...(invId ? {...state} : {}),
        childrenIds: [],
        // image: state.image,
        parentId: parentId,
        asset: {
          _id: 'mock',
          name: name,
          type: state.asset.type,
          subtypes: state.asset.subtypes,
          parentId: parentId,
          // image: state.image,
          ledger: { sharePrice: { value: 0, type: 'SEK' }, sharesIssued: 0 },
          contactEmail: '',
          listed: state.asset.listed,
          ...(invId ? {...state.asset} : {}),
        }
      }

      return (
        <OwnershipTree
          isPreview
          scrollable
          exclusive
          key={`owned-by-${state.parentId}-${imgId}-${invId}`}
          name={name}
          parentId={parentId}
          mockInvestment={mock}
          selectedId={invId || 'mock'}
          childId={invId || 'mock'}
          realParentId={state.originalParentId}
        />
      )
    }
  }
}

export const ownership: UseAssetFormLayout = {
  children: (_state) => [
    { render: 'ownedByCompany' },
    ...(_state.ownedByCompany
      ? [
          {
            children: (state: UseAssetFormState) =>
              state.ownedByCompany
                ? [
                    !!state.ownedByCompany && { render: 'parentId' },
                    !!state.ownedByCompany && !!state.parentId && ownershipStructure
                  ]
                : []
          }
        ]
      : [])
  ]
}

export const addressLayout: UseAssetFormLayout = {
  children: [
    {
      attributes: { class: 'grid grid-cols-2 gap-4'},
      children: [
        { render: 'asset.address' },
        { render: 'asset.zip' },
        { render: 'asset.city' },
        { render: 'asset.country' }
      ]
    }
  ]
}
